import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { ReferralToolkit } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, Check, Loader2, Megaphone } from 'lucide-react';

function CopyRow({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable; the text is selectable as a fallback
      setCopied(false);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      <div className="flex items-start gap-2 rounded-lg border bg-gray-50 p-3">
        <code className={`flex-1 min-w-0 text-sm text-gray-800 ${multiline ? 'whitespace-pre-wrap' : 'break-all'}`}>
          {value}
        </code>
        <Button type="button" variant="outline" size="sm" onClick={copy} className="flex-shrink-0">
          {copied ? <Check className="h-4 w-4 mr-1 text-green-600" /> : <Copy className="h-4 w-4 mr-1" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

export function ReferralToolkitPage() {
  const [toolkit, setToolkit] = useState<ReferralToolkit | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getReferralToolkit()
      .then(setToolkit)
      .catch((err: { error?: string }) => setError(err.error || 'Could not load your toolkit'))
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Referral Toolkit</h1>
          <p className="text-gray-600 mt-1">Your link, your code, and what your referrals get</p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
          </div>
        ) : error || !toolkit ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-600">{error || 'Toolkit unavailable'}</CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Share your link</CardTitle>
                <CardDescription>
                  Anyone who signs up through your link or with your code is credited to you automatically.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {toolkit.referral_link ? (
                  <CopyRow label="Referral link" value={toolkit.referral_link} />
                ) : (
                  <p className="text-sm text-gray-600">
                    Your referral link is not set up yet. Contact Partner Support and we will add it.
                  </p>
                )}
                {toolkit.referral_code && (
                  <CopyRow label="Referral code, for phone or in-person signups" value={toolkit.referral_code} />
                )}
                {toolkit.share_note && <CopyRow label="Ready-to-send note" value={toolkit.share_note} multiline />}
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>What your referrals get</CardTitle>
                </CardHeader>
                <CardContent>
                  {toolkit.client_offer_text ? (
                    <p className="text-gray-900">{toolkit.client_offer_text}</p>
                  ) : (
                    <p className="text-sm text-gray-600">
                      No offer is attached to your code yet. Contact Partner Support to set one up.
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Materials</CardTitle>
                  <CardDescription>Logos, overviews and flyers you can share with the businesses you refer.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to="/sales-marketing">
                    <Button variant="outline">
                      <Megaphone className="h-4 w-4 mr-2" />
                      Sales &amp; Marketing
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
